'use client';

import { Bar } from 'react-chartjs-2';
import { Box, Typography } from '@wishket/design-system';

import { ItemContainer, Root, TypographyChip } from './Chart.parts';
import { GraphNumberFirstIcon } from './ArticleDetailStatisticsChart.parts';

import {
  ArticleDetailStatisticsChartProps,
  Skill,
} from './ArticleDetailStatisticsChart.types';

import {
  chartPlugins,
  getChartData,
  getChartOptions,
} from './ArticleDetailStatisticsChart.charts';

import { useDeviceCheck } from '@/shared/hooks';

const ArticleDetailStatisticsChart = ({
  data,
}: ArticleDetailStatisticsChartProps) => {
  const { mostHasSkills, firstSkill, secondSkill, thirdSkill } = data;

  const { isMobile } = useDeviceCheck();

  const chartOptions = getChartOptions(
    isMobile,
    firstSkill,
    secondSkill,
    thirdSkill
  );
  const chartData = getChartData(firstSkill, secondSkill, thirdSkill);

  const getYearForRender = (viewCount: Skill['viewCount']) => {
    const viewCountArray = Object.values(viewCount);
    const index = viewCountArray.indexOf(Math.max(...viewCountArray));

    return [
      { year: '1', particle: '년 이하' },
      { year: '2~4', particle: '년차' },
      { year: '5~7', particle: '년차' },
      { year: '8', particle: '년 이상' },
    ][index];
  };

  const { year, particle } = getYearForRender(firstSkill.viewCount);

  return (
    <Root>
      <Typography
        variant="body1"
        className="flex flex-wrap items-center gap-1.5"
        data-testid="article-detail-statistics-chart-title"
      >
        <TypographyChip>{year}</TypographyChip>
        {particle},<TypographyChip>{mostHasSkills[0]}</TypographyChip>,
        <TypographyChip>{firstSkill.name}</TypographyChip>
        <Typography variant="body1">가 많이 봤어요!</Typography>
      </Typography>
      <Box className="-mx-4 flex gap-4 overflow-x-scroll px-4 scrollbar-hide">
        <ItemContainer className="min-w-[272px] gap-5 p-4 tablet:min-w-[460px] tablet:p-5">
          <Box>
            <Box className="flex justify-between">
              <Typography
                variant="body2"
                className="font-medium text-w-gray-900"
              >
                직무 및 연차
              </Typography>
              <Box className="flex gap-1.5 tablet:gap-2">
                <Box className="flex items-center gap-1">
                  <Box className="size-1 rounded-full bg-primary-400" />
                  <Typography variant="caption2" className="text-w-gray-500">
                    {isMobile ? '1년' : '1년 이하'}
                  </Typography>
                </Box>
                <Box className="flex items-center gap-1">
                  <Box className="size-1 rounded-full bg-[#A054E8]" />
                  <Typography variant="caption2" className="text-w-gray-500">
                    2~4년차
                  </Typography>
                </Box>
                <Box className="flex items-center gap-1">
                  <Box className="size-1 rounded-full bg-w-green-300" />
                  <Typography variant="caption2" className="text-w-gray-500">
                    5~7년
                  </Typography>
                </Box>
                <Box className="flex items-center gap-1">
                  <Box className="size-1 rounded-full bg-yellow-500" />
                  <Typography variant="caption2" className="text-w-gray-500">
                    {isMobile ? '8년~' : '8년 이상'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className="h-[110px]">
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={chartPlugins}
              height={110}
            />
          </Box>
        </ItemContainer>
        <ItemContainer className="flex min-w-[184px] max-w-[184px] flex-col gap-5 p-4 tablet:p-5">
          <Typography variant="body2" className="text-w-gray-900">
            많이 본 직무의 기술
          </Typography>
          <Box
            className="flex flex-col gap-3"
            data-testid="article-detail-statistics-chart-skill"
          >
            {mostHasSkills.map((skill, index) => (
              <Box key={skill} className="flex gap-2">
                {index === 0 ? (
                  <GraphNumberFirstIcon />
                ) : (
                  <Typography
                    variant="body3"
                    className="h-4 w-4 text-center text-w-gray-600"
                  >
                    {index + 1}
                  </Typography>
                )}
                <Typography
                  variant="body3"
                  className={
                    index === 0 ? 'text-w-gray-900' : 'text-w-gray-500'
                  }
                >
                  {skill}
                </Typography>
              </Box>
            ))}
          </Box>
        </ItemContainer>
      </Box>
    </Root>
  );
};

export default ArticleDetailStatisticsChart;
